import './suppressErrors';
import './i18n/i18n';
import React from 'react';
import ReactDOM from 'react-dom/client';
import { AuthProvider, AuthProviderProps } from "react-oidc-context";
import './index.css';
import App from './App';
import reportWebVitals from './reportWebVitals';
import { User, WebStorageStateStore } from 'oidc-client-ts';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import CompanyAssessment, { companyAssessmentRuleSetLoader } from './Routes/CompanyAssessment/CompanyAssessment';
import CompanyAssessmentIndex from './Routes/CompanyAssessment/CompanyAssessmentIndex';
import ShortestPathCompanyAssessment from './Routes/ShortestPath/ShortestPathCompanyAssessment';
import RuleSets, { rulesLoader } from './Routes/Rules/RuleSets';
import UploadWatchlistPage from './Routes/ManageWatchlist/ManageWatchlist';
import CustomLinking from './Routes/CustomLinking/CustomLinking';
import RuleSetEditor, { ruleSetCreateAction, ruleSetEditAction, ruleSetEditorLoader } from './Routes/Rules/RuleSetEditor';
import CompanyProfileDisplay, { companyAssessmentLoader } from './Routes/CompanyAssessment/CompanyProfileDisplay';
import ShortestPathCompanyProfileDisplay, { ShortestPathcompanyAssessmentLoader } from './Routes/ShortestPath/ShortestPathCompanyProfileDisplay';
import ErrorBoundary from './ErrorBoundary';
import AppErrorBoundary from './components/ErrorBoundary';
import { Documentation } from 'org-graph';
import AddressSearch from './Routes/AddressSearch/AddressSearch';
import PersonSearch from './Routes/PersonSearch/PersonSearch';
import GraphMonitoringOverview from './Routes/GraphMonitoring/GraphMonitoringOverview';
import GraphMonitoringDetail from './Routes/GraphMonitoring/GraphMonitoringDetail';
import GraphMonitoringPortfolioDiff from './Routes/GraphMonitoring/GraphMonitoringPortfolioDiff';


const isValidRedirectUrl = (url: string): boolean => {
  try {
    const redirectURL = new URL(url);
    const currentOrigin = window.location.origin;
    
    // Only allow redirects within the same origin (same protocol, domain, and port)
    return redirectURL.origin === currentOrigin;
  } catch {
    // Invalid URL format
    return false;
  }
};

const onSigninCallback = (user: User | void): void => {
  const stateParam = user?.state as any;

  if (stateParam && user) {
    try {
      if (stateParam.redirectUrl && isValidRedirectUrl(stateParam.redirectUrl)) {
        // Clean up the URL first, then redirect immediately
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname
        );
        
        window.location.href = stateParam.redirectUrl;
        return;
      }
    } catch (error) {
      // If state parsing fails, continue with normal flow
      console.debug('Could not parse auth state:', error);
    }
  }
  
  window.history.replaceState(
    {},
    document.title,
    window.location.pathname
  )
}

const oidcConfig: AuthProviderProps = {
  authority: process.env.REACT_APP_OIDC_AUTHORITY ?? 'UNSET',
  client_id: process.env.REACT_APP_OIDC_CLIENT_ID ?? "UNSET",
  redirect_uri: process.env.REACT_APP_OIDC_REDIRECT_URI ?? window.location.origin,
  post_logout_redirect_uri: process.env.REACT_APP_OIDC_POST_LOGOUT_REDIRECT_URI ?? window.location.origin,
  scope: "openid company-profiles/read rule-sets/read rule-sets/write trusted-list/read trusted-list/write watch-list/read watch-list/write custom-relationships/read custom-relationships/write",
  onSigninCallback: onSigninCallback,
  userStore: new WebStorageStateStore({ store: window.localStorage }),
};

const router = createBrowserRouter([
  {
    path: "/",
    element: <App />,
    errorElement: <ErrorBoundary />,
    children: [
      {
        path: "/companyassessment",
        element: <CompanyAssessment />,
        errorElement: <ErrorBoundary />,
        loader: companyAssessmentRuleSetLoader,
        children: [
          {
            index: true,
            element: <CompanyAssessmentIndex />,
            errorElement: <ErrorBoundary />
          },
          {
            path: ":orgNumber",
            element: <CompanyProfileDisplay />,
            errorElement: <ErrorBoundary />,
            loader: companyAssessmentLoader
          }
        ]
      },{
        path: "/shortestpath",
        element: <ShortestPathCompanyAssessment />,
        errorElement: <ErrorBoundary />,
        children: [
          {
            path: ":orgNumber1/:orgNumber2",
            element: <ShortestPathCompanyProfileDisplay />,
            errorElement: <ErrorBoundary />,
            loader: ShortestPathcompanyAssessmentLoader
          }
        ]
      },
      {
        path: "/addresssearch",
        element: <AddressSearch />,
      },{
        path: "/personsearch",
        element: <PersonSearch />,
        errorElement: <ErrorBoundary />
      },
      {
        path: "/rules",
        element: <RuleSets />,
        errorElement: <ErrorBoundary />,
        loader: rulesLoader
      },
      {
        path: "/rules/create",
        element: <RuleSetEditor />,
        errorElement: <ErrorBoundary />,
        loader: ruleSetEditorLoader,
        action: ruleSetCreateAction
      },
      {
        path: "/rules/:ruleSetId",
        element: <RuleSetEditor />,
        errorElement: <ErrorBoundary />,
        loader: ruleSetEditorLoader,
        action: ruleSetEditAction
      },
      {
        path: "/ManageWatchlist",
        element: <UploadWatchlistPage />,
        errorElement: <ErrorBoundary />
      },
      {
        path: "/graphmonitoring",
        element: <GraphMonitoringOverview />,
        errorElement: <ErrorBoundary />
      },
      {
        path: "/graphmonitoring/portfolio-diff",
        element: <GraphMonitoringPortfolioDiff />,
        errorElement: <ErrorBoundary />
      },
      {
        path: "/graphmonitoring/:orgNumber",
        element: <GraphMonitoringDetail />,
        errorElement: <ErrorBoundary />
      },
      {
        path: "/CustomLinking",
        element: <CustomLinking />,
        errorElement: <ErrorBoundary />
      },
      {
        path: "/documentation",
        element: <Documentation />,
        errorElement: <ErrorBoundary />
      }
    ]
  }
]);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <AuthProvider {...oidcConfig}>
        <RouterProvider router={router} />
      </AuthProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
